<template>
  <c-dialog
    :value="visible"
    width="600"
    close-key="cove.general.cancel"
    title-key="page.manageFiling.docketEntry.requestFeeWaiver"
    @input="close">

    <validation-error-panel :error-scope="errorScope" class="ma-4 mb-2"/>

    <div v-if="visible" class="pa-4">
      <div class="c-caption">
        {{ $t('popup.feeWaiverRequest.selectReason') }}
      </div>
      <div class="text-minimum-gray">
        {{ $t('popup.feeWaiverRequest.selectReason.description') }}
      </div>

      <div class="mt-7">
        <c-autocomplete
          :label="$t('popup.feeWaiverRequest.field.feeWaiverReason')"
          v-model="selectedFeeWaiverReason"
          :items="feeWaiverReasons"
          item-text="displayName"
          :key="`${errorScope}:feeWaiverReason`"
          return-object
          outlined
          required
          hide-details="auto"
          aria-describedby="afb0947c-6361-47a8-a770-a6e1a8e03f08">
          <template #item="{ item }">
            <v-list-item-content class="d-block">
              <v-list-item-title class="font-weight-medium">
                {{ item.displayName }}
              </v-list-item-title>
              <div class="d-flex text-minimum-gray font-95" style="max-width:70ch">
                {{ item.description }}
              </div>
            </v-list-item-content>
          </template>
        </c-autocomplete>
        <field-description id="afb0947c-6361-47a8-a770-a6e1a8e03f08"
                           message-key="popup.feeWaiverRequest.field.feeWaiverReason.description"/>
      </div>
    </div>

    <template #actions-right>
      <c-btn type="primary" @click="save">
        {{ $t('global.save') }}
      </c-btn>
    </template>
  </c-dialog>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import FieldDescription from '@components/support/FieldDescription';
import ValidationErrorPanel from '@components/support/ValidationErrorPanel';
import Filing from '@/application/filing';
import { useFilingStore } from '@/stores/filingStore';

const filingStore = useFilingStore();

export default {
  components: { ValidationErrorPanel, FieldDescription },
  props: {
    value: {
      type: Boolean
    },
    editFeeWaiverID: {
      type: String
    }
  },
  data () {
    return {
      loading: false,
      errorScope: 'feeWaiverPopup',
      feeWaiverReasons: null,
      selectedFeeWaiverReason: null
    };
  },
  computed: {
    visible () {
      return this.value && !this.loading;
    },
    filingID () {
      return filingStore.filingID;
    }
  },
  watch: {
    value (newValue) {
      if (newValue)
      {
        // prep the popup for display
        this.$cove.block();
        this.loading = true;
        this.loadFeeWaiverReasons()
          .finally(() => {
            this.loading = false;
            this.$cove.unblock();
          });
      }
      else
      {
        // reset all the data
        this.feeWaiverReasons = null;
        this.selectedFeeWaiverReason = null;
        this.$cove.validation.clear(this.errorScope);
      }
    }
  },
  methods: {
    loadFeeWaiverReasons () {
      return axios.get(this.$cove.getAPI({ name: 'api.filing.feeWaiverReasons' }))
        .then((response) => {
          this.feeWaiverReasons = _.get(response, 'data._embedded.results', []);

          // if we are opening in 'edit' mode, preselect the current value
          if (!_.isNil(this.editFeeWaiverID))
          {
            this.selectedFeeWaiverReason = _.defaultTo(_.find(this.feeWaiverReasons, { resourceID: this.editFeeWaiverID }), null);
          }
        });
    },
    validate () {
      this.$cove.validation.startScope(this.errorScope);
      this.$cove.validation.clear();

      this.$cove.validation.required(this.selectedFeeWaiverReason, 'popup.feeWaiverRequest.field.feeWaiverReason', 'feeWaiverReason');

      let isValid = this.$cove.validation.isValid();
      this.$cove.validation.endScope();

      return isValid;
    },
    save () {
      this.$cove.blockNow();

      let isValid = this.validate();
      if (!isValid)
      {
        this.$cove.unblock();
        return;
      }

      let payload = {
        feeWaiverReasonID: this.selectedFeeWaiverReason.resourceID
      };

      Filing.patchFilingInfo({ filingID: this.filingID, payload })
        .then(() => {
          this.$cove.notify({ message: this.$t('application.save.success'), color: 'success' });
          this.$emit('save');
          this.close();
        })
        .finally(() => {
          this.$cove.unblock();
        });
    },
    close () {
      this.$emit('input', false);
    }
  }
};
</script>